import React, { useState, useEffect, useCallback } from 'react';
import { 
  Key, 
  ShieldSlash, 
  DownloadSimple, 
  GameController,
  FolderOpen,
  SignIn,
  Folder,
  HardDrive,
  Play,
  ArrowRight,
  Check,
  X,
  Warning,
  Info,
  CheckCircle,
  ArrowSquareOut,
  Lightbulb,
  Clock,
  Question,
  DiscordLogo,
  Book
} from '@phosphor-icons/react';
import { api } from '../utils/api';
import './Tutorial.css';

interface TutorialProps {
  username: string;
  onComplete: () => void;
}

type StepKey = 
  | 'claim-license'
  | 'disable-antivirus'
  | 'download-volt'
  | 'force-roblox'
  | 'extract-files'
  | 'open-volt'
  | 'browse-folder'
  | 'install-volt'
  | 'launch-roblox'
  | 'complete';

interface StepConfig {
  key: StepKey;
  label: string;
  icon: React.ReactNode;
}

const STEP_CONFIG: StepConfig[] = [
  { key: 'claim-license', label: 'Claim License', icon: <Key weight="bold" /> },
  { key: 'disable-antivirus', label: 'Disable Antivirus', icon: <ShieldSlash weight="bold" /> },
  { key: 'download-volt', label: 'Download Volt', icon: <DownloadSimple weight="bold" /> },
  { key: 'force-roblox', label: 'Force Roblox', icon: <GameController weight="bold" /> },
  { key: 'extract-files', label: 'Extract Files', icon: <FolderOpen weight="bold" /> },
  { key: 'open-volt', label: 'Open Volt', icon: <SignIn weight="bold" /> },
  { key: 'browse-folder', label: 'Set Roblox Path', icon: <Folder weight="bold" /> },
  { key: 'install-volt', label: 'Install Files', icon: <HardDrive weight="bold" /> },
  { key: 'launch-roblox', label: 'Launch Roblox', icon: <Play weight="bold" /> },
];

const Tutorial: React.FC<TutorialProps> = ({ username, onComplete }) => {
  // Load saved progress from localStorage
  const getSavedProgress = () => {
    const saved = localStorage.getItem('tutorialProgress');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        return {
          stepIndex: parsed.stepIndex || 0,
          completedSteps: new Set<number>(parsed.completedSteps || []),
          licenseKey: parsed.licenseKey || ''
        };
      } catch {
        return { stepIndex: 0, completedSteps: new Set<number>(), licenseKey: '' };
      }
    }
    return { stepIndex: 0, completedSteps: new Set<number>(), licenseKey: '' };
  };

  const savedProgress = getSavedProgress();
  
  const [stepIndex, setStepIndex] = useState(savedProgress.stepIndex);
  const [isExiting, setIsExiting] = useState(false);
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(savedProgress.completedSteps);
  
  // License state
  const [licenseKey, setLicenseKey] = useState(savedProgress.licenseKey);
  const [licenseStatus, setLicenseStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [licenseMessage, setLicenseMessage] = useState('');
  const [hasExistingSubscription, setHasExistingSubscription] = useState(false);
  const [checkingSubscription, setCheckingSubscription] = useState(true);
  
  // License claiming overlay state
  const [showClaimOverlay, setShowClaimOverlay] = useState(false);
  const [claimProgress, setClaimProgress] = useState(0);
  const [claimStage, setClaimStage] = useState<'verifying' | 'activating' | 'success'>('verifying');
  
  // Roblox version state
  const [rbxVersion, setRbxVersion] = useState<string | null>(null);
  const [versionLoading, setVersionLoading] = useState(true);
  const [discordLink, setDiscordLink] = useState<string | null>(null);

  // Save progress to localStorage whenever it changes
  useEffect(() => {
    const progress = {
      stepIndex,
      completedSteps: Array.from(completedSteps),
      licenseKey
    };
    localStorage.setItem('tutorialProgress', JSON.stringify(progress));
  }, [stepIndex, completedSteps, licenseKey]);

  const currentStep = stepIndex < STEP_CONFIG.length ? STEP_CONFIG[stepIndex].key : 'complete';
  const progress = Math.round((stepIndex / STEP_CONFIG.length) * 100);

  useEffect(() => {
    const fetchVersion = async () => {
      try {
        const res = await fetch('https://proxy-api-taupe.vercel.app/api/status/exploits/volt');
        const data = await res.json();
        if (data.rbxversion) setRbxVersion(data.rbxversion);
        if (data.discordlink) setDiscordLink(data.discordlink);
      } catch {
        // Version fetch failed silently
      } finally {
        setVersionLoading(false);
      }
    };
    fetchVersion();
  }, []);

  // Check if user already has an active subscription (from registration)
  useEffect(() => {
    const checkExistingSubscription = async () => {
      try {
        const subRes = await api.get('https://api.volt.bz/licenses/subscriptions');
        if (subRes.ok) {
          const subData = await subRes.json();
          const activeSub = subData.data?.find((sub: { status: string }) => sub.status === 'active');
          if (activeSub) {
            setHasExistingSubscription(true);
            // Auto-complete the license step if on step 0
            if (stepIndex === 0) {
              setLicenseStatus('success');
              setLicenseMessage('License already active from registration!');
              setCompletedSteps(prev => new Set(Array.from(prev).concat(0)));
            }
          }
        }
      } catch {
        // Failed to check subscription, continue normally
      } finally {
        setCheckingSubscription(false);
      }
    };
    checkExistingSubscription();
  }, []);

  const goNext = useCallback(() => {
    setIsExiting(true);
    setCompletedSteps(prev => new Set(Array.from(prev).concat(stepIndex)));
    setTimeout(() => {
      setStepIndex((prev: number) => prev + 1);
      setIsExiting(false);
    }, 300);
  }, [stepIndex]);

  const goToStep = useCallback((index: number) => {
    if (index === stepIndex) return;
    setIsExiting(true);
    setTimeout(() => {
      setStepIndex(index);
      setIsExiting(false);
    }, 300);
  }, [stepIndex]);

  const claimLicense = async () => {
    if (!licenseKey.trim()) {
      setLicenseStatus('error');
      setLicenseMessage('Please enter a license key');
      return;
    }

    // Show the overlay and start the animation
    setShowClaimOverlay(true);
    setClaimProgress(0);
    setClaimStage('verifying');
    setLicenseStatus('loading');
    setLicenseMessage('Claiming license...');

    // Test mode - simulate success without API call
    const isTestMode = licenseKey.trim().toLowerCase() === 'experimentaltest987';

    // Animate progress bar while verifying
    const progressInterval = setInterval(() => {
      setClaimProgress(prev => {
        if (prev >= 40) return prev; // Pause at 40% while waiting for API
        return prev + 2;
      });
    }, 50);

    // Simulate delay for test mode
    if (isTestMode) {
      await new Promise(resolve => setTimeout(resolve, 1500));
      clearInterval(progressInterval);
      
      // Continue progress animation for "activating" stage
      setClaimStage('activating');
      
      const activateInterval = setInterval(() => {
        setClaimProgress(prev => {
          if (prev >= 90) {
            clearInterval(activateInterval);
            return 90;
          }
          return prev + 3;
        });
      }, 40);

      // Wait a bit then show success
      setTimeout(() => {
        clearInterval(activateInterval);
        setClaimProgress(100);
        setClaimStage('success');
        setLicenseStatus('success');
        setLicenseMessage('License activated successfully');
        
        // After showing success, close overlay and go next
        setTimeout(() => {
          setShowClaimOverlay(false);
          setTimeout(goNext, 300);
        }, 2500);
      }, 1200);
      
      return;
    }

    try {
      // Use api utility which handles token refresh automatically
      const res = await api.post('https://api.volt.bz/licenses/claim', { key: licenseKey });
      const data = await res.json();

      clearInterval(progressInterval);

      if (res.ok) {
        // Continue progress animation for "activating" stage
        setClaimStage('activating');
        
        const activateInterval = setInterval(() => {
          setClaimProgress(prev => {
            if (prev >= 90) {
              clearInterval(activateInterval);
              return 90;
            }
            return prev + 3;
          });
        }, 40);

        // Wait a bit then show success
        setTimeout(() => {
          clearInterval(activateInterval);
          setClaimProgress(100);
          setClaimStage('success');
          setLicenseStatus('success');
          setLicenseMessage('License activated successfully');
          
          // After showing success, close overlay and go next
          setTimeout(() => {
            setShowClaimOverlay(false);
            setTimeout(goNext, 300);
          }, 2500);
        }, 1200);
      } else {
        clearInterval(progressInterval);
        setShowClaimOverlay(false);
        setLicenseStatus('error');
        setLicenseMessage(data.message || 'Invalid license key');
      }
    } catch {
      clearInterval(progressInterval);
      setShowClaimOverlay(false);
      setLicenseStatus('error');
      setLicenseMessage('Connection failed. Try again.');
    }
  };

  const downloadVolt = () => {
    window.open('https://api.volt.bz/version/download/ui', '_blank');
  };

  const downloadRoblox = () => {
    if (rbxVersion) {
      window.open(`https://rdd.weao.gg/?channel=LIVE&binaryType=WindowsPlayer&version=${rbxVersion}`, '_blank');
    }
  };

  const skipTutorial = () => {
    if (window.confirm('Skip setup? You can access this guide later from settings.')) {
      onComplete();
    }
  };

  const renderSidebar = () => (
    <aside className="tutorial-sidebar">
      <div className="sidebar-header">
        <img src="/bolt-full.svg" alt="Volt" className="sidebar-logo" />
      </div>
      
      <h3 className="sidebar-title">Setup Guide</h3>
      
      <ul className="step-list">
        {STEP_CONFIG.map((step, index) => (
          <li 
            key={step.key} 
            className={`step-item ${index === stepIndex ? 'active' : ''} ${completedSteps.has(index) ? 'completed' : ''}`}
            onClick={() => goToStep(index)}
          >
            <div className="step-indicator">
              {completedSteps.has(index) ? <Check weight="bold" /> : index + 1}
            </div>
            <span className="step-label">{step.label}</span>
          </li>
        ))}
      </ul>
      
      <div className="sidebar-progress">
        <div className="progress-label">
          <span className="progress-text">Progress</span>
          <span className="progress-percent">{progress}%</span>
        </div>
        <div className="progress-bar">
          <div className="progress-fill" style={{ width: `${progress}%` }} />
        </div>
        
        <button className="skip-btn" onClick={skipTutorial}>
          <ArrowSquareOut weight="bold" />
          Skip setup
        </button>
      </div>
    </aside>
  );

  const renderStepContent = () => {
    switch (currentStep) {
      case 'claim-license':
        // If user already has subscription from registration, show success state
        if (hasExistingSubscription) {
          return (
            <>
              <div className="step-header">
                <div className="step-header-top">
                  <div className="step-icon-box success">
                    <CheckCircle weight="bold" />
                  </div>
                  <div className="step-header-text">
                    <div className="step-tag">Step 1 of 9</div>
                    <h2 className="step-title">License Already Active!</h2>
                  </div>
                </div>
              </div>
              
              <div className="step-card">
                <div className="step-card-header">
                  <h3 className="step-card-header-title">License Activation</h3>
                </div>
                <div className="step-card-body">
                  <div className="success-message">
                    <CheckCircle weight="bold" className="success-icon" />
                    <div className="success-text">
                      <h4>You're all set, {username}!</h4>
                      <p>Your license was automatically activated when you registered. Let's continue with the setup.</p>
                    </div>
                  </div>
                </div>
                
                <div className="step-card-footer">
                  <button 
                    className="btn-claim-license"
                    onClick={goNext}
                  >
                    Continue Setup
                    <ArrowRight weight="bold" />
                  </button>
                </div>
              </div>
            </>
          );
        }
        
        // Show loading state while checking subscription
        if (checkingSubscription) {
          return (
            <>
              <div className="step-header">
                <div className="step-header-top">
                  <div className="step-icon-box">
                    <Key weight="bold" />
                  </div>
                  <div className="step-header-text">
                    <div className="step-tag">Step 1 of 9</div>
                    <h2 className="step-title">Checking License...</h2>
                  </div>
                </div>
              </div>
              
              <div className="step-card">
                <div className="step-card-body" style={{ textAlign: 'center', padding: '3rem' }}>
                  <div className="spinner" style={{ margin: '0 auto 1rem' }} />
                  <p style={{ color: '#71717a' }}>Checking your license status...</p>
                </div>
              </div>
            </>
          );
        }
        
        // Normal license claim flow
        return (
          <>
            <div className="step-header">
              <div className="step-header-top">
                <div className="step-icon-box">
                  <Key weight="bold" />
                </div>
                <div className="step-header-text">
                  <div className="step-tag">Step 1 of 9</div>
                  <h2 className="step-title">Claim Your License</h2>
                </div>
              </div>
            </div>
            
            <div className="step-card">
              <div className="step-card-header">
                <h3 className="step-card-header-title">License Activation</h3>
              </div>
              <div className="step-card-body">
                <p className="step-description">
                  Hey <strong>{username}</strong>, enter your license key below to activate Volt on your account.
                </p>
                
                <div className="form-group">
                  <label className="form-label">License Key</label>
                  <div className="license-input-wrapper">
                    <input
                      type="text"
                      className="license-input"
                      placeholder="XXXX-XXXX-XXXX-XXXX"
                      value={licenseKey}
                      onChange={(e) => {
                        setLicenseKey(e.target.value.toUpperCase());
                        if (licenseStatus !== 'idle') setLicenseStatus('idle');
                      }}
                      disabled={licenseStatus === 'loading' || licenseStatus === 'success'}
                    />
                  </div>
                </div>
                
                {licenseStatus !== 'idle' && (
                  <div className={`status-badge ${licenseStatus}`}>
                    {licenseStatus === 'loading' && <div className="spinner" />}
                    {licenseStatus === 'success' && <Check weight="bold" />}
                    {licenseStatus === 'error' && <X weight="bold" />}
                    {licenseMessage}
                  </div>
                )}
              </div>
              
              <div className="step-card-footer">
                <button 
                  className="btn-claim-license"
                  onClick={claimLicense}
                  disabled={licenseStatus === 'loading' || licenseStatus === 'success'}
                >
                  Claim License
                  <ArrowRight weight="bold" />
                </button>
                <button 
                  className="btn-skip-text"
                  onClick={goNext}
                >
                  I don't have a license key, skip for now
                </button>
              </div>
            </div>
          </>
        );

      case 'disable-antivirus':
        return (
          <>
            <div className="step-header">
              <div className="step-header-top">
                <div className="step-icon-box">
                  <ShieldSlash weight="bold" />
                </div>
                <div className="step-header-text">
                  <div className="step-tag">Step 2 of 9</div>
                  <h2 className="step-title">Disable Your Antivirus</h2>
                </div>
              </div>
            </div>
            
            <div className="step-card">
              <div className="step-card-header">
                <h3 className="step-card-header-title">Security Settings</h3>
              </div>
              <div className="step-card-body">
                <p className="step-description">
                  Temporarily disable real-time protection to prevent installation interruptions. You can re-enable it after setup.
                </p>
                
                <div className="alert-box warning">
                  <div className="alert-icon">
                    <Warning weight="fill" />
                  </div>
                  <div className="alert-content">
                    <h4>Why is this needed?</h4>
                    <p>Antivirus software may incorrectly flag Volt as a threat. This is a false positive.</p>
                  </div>
                </div>

                <ul className="instructions-list">
                  <li className="instruction-item">
                    <span className="instruction-num">1</span>
                    <div className="instruction-content">
                      Open <strong>Windows Security</strong> from the Start menu
                    </div>
                  </li>
                  <li className="instruction-item">
                    <span className="instruction-num">2</span>
                    <div className="instruction-content">
                      Navigate to <strong>Virus & threat protection</strong> → <strong>Manage settings</strong>
                    </div>
                  </li>
                  <li className="instruction-item">
                    <span className="instruction-num">3</span>
                    <div className="instruction-content">
                      Toggle off <strong>Real-time protection</strong>
                    </div>
                  </li>
                </ul>
              </div>
              
              <div className="step-card-footer">
                <div className="step-actions">
                  <button className="btn-primary" onClick={goNext}>
                    Continue
                    <ArrowRight weight="bold" />
                  </button>
                </div>
              </div>
            </div>
          </>
        );

      case 'download-volt':
        return (
          <>
            <div className="step-header">
              <div className="step-header-top">
                <div className="step-icon-box">
                  <DownloadSimple weight="bold" />
                </div>
                <div className="step-header-text">
                  <div className="step-tag">Step 3 of 9</div>
                  <h2 className="step-title">Download Volt</h2>
                </div>
              </div>
            </div>
            
            <div className="step-card">
              <div className="step-card-header">
                <h3 className="step-card-header-title">Download Files</h3>
              </div>
              <div className="step-card-body">
                <p className="step-description">
                  Download the latest version of Volt. Save it somewhere easy to find like your Desktop or Downloads folder.
                </p>
                
                <button className="download-btn" onClick={downloadVolt}>
                  <DownloadSimple weight="bold" />
                  Download Volt
                </button>

                <div className="alert-box info">
                  <div className="alert-icon">
                    <Info weight="fill" />
                  </div>
                  <div className="alert-content">
                    <h4>Pro tip</h4>
                    <p>Keep Volt in a dedicated folder so it's easy to find later.</p>
                  </div>
                </div>
              </div>
              
              <div className="step-card-footer">
                <div className="step-actions">
                  <button className="btn-primary" onClick={goNext}>
                    Continue
                    <ArrowRight weight="bold" />
                  </button>
                </div>
              </div>
            </div>
          </>
        );

      case 'force-roblox':
        return (
          <>
            <div className="step-header">
              <div className="step-header-top">
                <div className="step-icon-box">
                  <GameController weight="bold" />
                </div>
                <div className="step-header-text">
                  <div className="step-tag">Step 4 of 9</div>
                  <h2 className="step-title">Download Forced Roblox</h2>
                </div>
              </div>
            </div>
            
            <div className="step-card">
              <div className="step-card-header">
                <h3 className="step-card-header-title">Roblox Version</h3>
              </div>
              <div className="step-card-body">
                <p className="step-description">
                  Download the specific Roblox version that Volt is compatible with. This ensures everything works correctly.
                </p>
                
                <div className="version-display">
                  <span className="version-text">Supported Version</span>
                  {versionLoading ? (
                    <span className="version-loading">
                      <div className="spinner" />
                      Loading...
                    </span>
                  ) : (
                    <span className="version-code">{rbxVersion || 'Unknown'}</span>
                  )}
                </div>

                <button 
                  className="download-btn" 
                  onClick={downloadRoblox}
                  disabled={!rbxVersion}
                >
                  <DownloadSimple weight="bold" />
                  Download Roblox
                </button>

                <div className="alert-box warning">
                  <div className="alert-icon">
                    <Warning weight="fill" />
                  </div>
                  <div className="alert-content">
                    <h4>Keep this in mind</h4>
                    <p>When Volt or Roblox updates, return here to download the new compatible version.</p>
                  </div>
                </div>
              </div>
              
              <div className="step-card-footer">
                <div className="step-actions">
                  <button className="btn-primary" onClick={goNext}>
                    Continue
                    <ArrowRight weight="bold" />
                  </button>
                </div>
              </div>
            </div>
          </>
        );

      case 'extract-files':
        return (
          <>
            <div className="step-header">
              <div className="step-header-top">
                <div className="step-icon-box">
                  <FolderOpen weight="bold" />
                </div>
                <div className="step-header-text">
                  <div className="step-tag">Step 5 of 9</div>
                  <h2 className="step-title">Extract the Files</h2>
                </div>
              </div>
            </div>
            
            <div className="step-card">
              <div className="step-card-header">
                <h3 className="step-card-header-title">File Extraction</h3>
              </div>
              <div className="step-card-body">
                <p className="step-description">
                  Create a new folder and extract all the Roblox files you just downloaded into it.
                </p>

                <ul className="instructions-list">
                  <li className="instruction-item">
                    <span className="instruction-num">1</span>
                    <div className="instruction-content">
                      Right-click on your <strong>Desktop</strong> and create a new folder
                    </div>
                  </li>
                  <li className="instruction-item">
                    <span className="instruction-num">2</span>
                    <div className="instruction-content">
                      Name it something like <code>VoltRoblox</code>
                    </div>
                  </li>
                  <li className="instruction-item">
                    <span className="instruction-num">3</span>
                    <div className="instruction-content">
                      Extract <strong>all contents</strong> from the downloaded ZIP into this folder
                    </div>
                  </li>
                </ul>

                <div className="alert-box info">
                  <div className="alert-icon">
                    <Info weight="fill" />
                  </div>
                  <div className="alert-content">
                    <h4>Important</h4>
                    <p>Make sure files are directly in the folder, not inside a subfolder.</p>
                  </div>
                </div>
              </div>
              
              <div className="step-card-footer">
                <div className="step-actions">
                  <button className="btn-primary" onClick={goNext}>
                    Continue
                    <ArrowRight weight="bold" />
                  </button>
                </div>
              </div>
            </div>
          </>
        );

      case 'open-volt':
        return (
          <>
            <div className="step-header">
              <div className="step-header-top">
                <div className="step-icon-box">
                  <SignIn weight="bold" />
                </div>
                <div className="step-header-text">
                  <div className="step-tag">Step 6 of 9</div>
                  <h2 className="step-title">Open Volt & Login</h2>
                </div>
              </div>
            </div>
            
            <div className="step-card">
              <div className="step-card-header">
                <h3 className="step-card-header-title">Application Login</h3>
              </div>
              <div className="step-card-body">
                <p className="step-description">
                  Launch the Volt application and sign in with your account credentials.
                </p>

                <ul className="instructions-list">
                  <li className="instruction-item">
                    <span className="instruction-num">1</span>
                    <div className="instruction-content">
                      Open the <strong>Volt</strong> application you downloaded
                    </div>
                  </li>
                  <li className="instruction-item">
                    <span className="instruction-num">2</span>
                    <div className="instruction-content">
                      Enter your <strong>username</strong> and <strong>password</strong>
                    </div>
                  </li>
                  <li className="instruction-item">
                    <span className="instruction-num">3</span>
                    <div className="instruction-content">
                      Click <strong>Login</strong> to authenticate
                    </div>
                  </li>
                </ul>
              </div>
              
              <div className="step-card-footer">
                <div className="step-actions">
                  <button className="btn-primary" onClick={goNext}>
                    Continue
                    <ArrowRight weight="bold" />
                  </button>
                </div>
              </div>
            </div>
          </>
        );

      case 'browse-folder':
        return (
          <>
            <div className="step-header">
              <div className="step-header-top">
                <div className="step-icon-box">
                  <Folder weight="bold" />
                </div>
                <div className="step-header-text">
                  <div className="step-tag">Step 7 of 9</div>
                  <h2 className="step-title">Set Roblox Path</h2>
                </div>
              </div>
            </div>
            
            <div className="step-card">
              <div className="step-card-header">
                <h3 className="step-card-header-title">Path Configuration</h3>
              </div>
              <div className="step-card-body">
                <p className="step-description">
                  Point Volt to the folder where you extracted the Roblox files.
                </p>

                <ul className="instructions-list">
                  <li className="instruction-item">
                    <span className="instruction-num">1</span>
                    <div className="instruction-content">
                      Find the <strong>Roblox Installation</strong> section in Volt
                    </div>
                  </li>
                  <li className="instruction-item">
                    <span className="instruction-num">2</span>
                    <div className="instruction-content">
                      Click the <strong>Browse</strong> button
                    </div>
                  </li>
                  <li className="instruction-item">
                    <span className="instruction-num">3</span>
                    <div className="instruction-content">
                      Select your <code>VoltRoblox</code> folder
                    </div>
                  </li>
                </ul>

                <div className="alert-box warning">
                  <div className="alert-icon">
                    <Warning weight="fill" />
                  </div>
                  <div className="alert-content">
                    <h4>Select the correct folder</h4>
                    <p>Choose the folder containing the files directly, not a parent folder.</p>
                  </div>
                </div>
              </div>
              
              <div className="step-card-footer">
                <div className="step-actions">
                  <button className="btn-primary" onClick={goNext}>
                    Continue
                    <ArrowRight weight="bold" />
                  </button>
                </div>
              </div>
            </div>
          </>
        );

      case 'install-volt':
        return (
          <>
            <div className="step-header">
              <div className="step-header-top">
                <div className="step-icon-box">
                  <HardDrive weight="bold" />
                </div>
                <div className="step-header-text">
                  <div className="step-tag">Step 8 of 9</div>
                  <h2 className="step-title">Install Volt Files</h2>
                </div>
              </div>
            </div>
            
            <div className="step-card">
              <div className="step-card-header">
                <h3 className="step-card-header-title">Installation</h3>
              </div>
              <div className="step-card-body">
                <p className="step-description">
                  Complete the installation by clicking the Install button in Volt.
                </p>

                <ul className="instructions-list">
                  <li className="instruction-item">
                    <span className="instruction-num">1</span>
                    <div className="instruction-content">
                      Locate and click the <strong>Install Now</strong> button
                    </div>
                  </li>
                  <li className="instruction-item">
                    <span className="instruction-num">2</span>
                    <div className="instruction-content">
                      Wait for the installation to complete
                    </div>
                  </li>
                  <li className="instruction-item">
                    <span className="instruction-num">3</span>
                    <div className="instruction-content">
                      You should see a <strong>success</strong> message when done
                    </div>
                  </li>
                </ul>
              </div>
              
              <div className="step-card-footer">
                <div className="step-actions">
                  <button className="btn-primary" onClick={goNext}>
                    Continue
                    <ArrowRight weight="bold" />
                  </button>
                </div>
              </div>
            </div>
          </>
        );

      case 'launch-roblox':
        return (
          <>
            <div className="step-header">
              <div className="step-header-top">
                <div className="step-icon-box">
                  <Play weight="bold" />
                </div>
                <div className="step-header-text">
                  <div className="step-tag">Step 9 of 9</div>
                  <h2 className="step-title">Launch Roblox</h2>
                </div>
              </div>
            </div>
            
            <div className="step-card">
              <div className="step-card-header">
                <h3 className="step-card-header-title">Final Step</h3>
              </div>
              <div className="step-card-body">
                <p className="step-description">
                  Always use the forced Roblox version you downloaded to ensure Volt works properly.
                </p>

                <ul className="instructions-list">
                  <li className="instruction-item">
                    <span className="instruction-num">1</span>
                    <div className="instruction-content">
                      Go to your <code>VoltRoblox</code> folder on Desktop
                    </div>
                  </li>
                  <li className="instruction-item">
                    <span className="instruction-num">2</span>
                    <div className="instruction-content">
                      Open <code>RobloxPlayerBeta.exe</code>
                    </div>
                  </li>
                  <li className="instruction-item">
                    <span className="instruction-num">3</span>
                    <div className="instruction-content">
                      <strong>Never</strong> use the original Roblox launcher
                    </div>
                  </li>
                </ul>

                <div className="alert-box warning">
                  <div className="alert-icon">
                    <Warning weight="fill" />
                  </div>
                  <div className="alert-content">
                    <h4>Important reminder</h4>
                    <p>The original Roblox may auto-update to an unsupported version. Always use the forced version.</p>
                  </div>
                </div>
              </div>
              
              <div className="step-card-footer">
                <div className="step-actions">
                  <button className="btn-primary" onClick={goNext}>
                    Complete Setup
                    <ArrowRight weight="bold" />
                  </button>
                </div>
              </div>
            </div>
          </>
        );

      case 'complete':
        return (
          <div className="complete-container">
            <div className="complete-checkmark">
              <div className="checkmark-circle">
                <CheckCircle weight="fill" />
              </div>
            </div>
            <h2 className="complete-title">You're all set!</h2>
            <p className="complete-subtitle">
              Volt has been configured successfully. Time to have some fun.
            </p>
            <button className="btn-dashboard" onClick={onComplete}>
              Enter Dashboard
              <ArrowRight weight="bold" />
            </button>
          </div>
        );

      default:
        return null;
    }
  };

  const renderInfoPanel = () => {
    return (
      <aside className="tutorial-info-panel">
        <div className="info-section">
          <h4 className="info-title">
            <Question weight="bold" />
            Need Help?
          </h4>
          <a href="https://discord.gg/6nEEDFZwhr" target="_blank" rel="noopener noreferrer" className="quick-link">
            <DiscordLogo weight="bold" />
            Support Server
          </a>
          <a href={discordLink || 'https://discord.gg/volt'} target="_blank" rel="noopener noreferrer" className="quick-link">
            <DiscordLogo weight="bold" />
            Volt Official
          </a>
          <a href="https://docs.volt.bz" target="_blank" rel="noopener noreferrer" className="quick-link">
            <Book weight="bold" />
            Documentation
          </a>
        </div>
      </aside>
    );
  };

  return (
    <div className="tutorial-container">
      <div className="tutorial-bg-accent" />
      
      {/* License Claim Overlay */}
      {showClaimOverlay && (
        <div className="license-claim-overlay">
          <div className="claim-overlay-content">
            {claimStage !== 'success' ? (
              <>
                <div className="claim-logo-container">
                  <img src="/bolt-full.svg" alt="Volt" className="claim-logo" />
                  <div className="claim-logo-ring" />
                  <div className="claim-logo-ring delay" />
                </div>
                
                <div className="claim-status">
                  <span className="claim-status-text">
                    {claimStage === 'verifying' ? 'Verifying License Key...' : 'Activating License...'}
                  </span>
                </div>
                
                <div className="claim-progress-container">
                  <div className="claim-progress-bar">
                    <div 
                      className="claim-progress-fill" 
                      style={{ width: `${claimProgress}%` }}
                    />
                    <div className="claim-progress-glow" style={{ left: `${claimProgress}%` }} />
                  </div>
                  <div className="claim-progress-text">{claimProgress}%</div>
                </div>
                
                <div className="claim-key-display">
                  <Key weight="bold" />
                  <span>{licenseKey}</span>
                </div>
              </>
            ) : (
              <div className="claim-success">
                <div className="claim-success-icon">
                  <div className="success-circle">
                    <CheckCircle weight="fill" />
                  </div>
                  <div className="success-particles">
                    {[...Array(12)].map((_, i) => (
                      <div key={i} className="success-particle" style={{ '--i': i } as React.CSSProperties} />
                    ))}
                  </div>
                </div>
                
                <h2 className="claim-success-title">License Activated!</h2>
                <p className="claim-success-subtitle">Welcome to Volt, {username}</p>
                
                <div className="claim-success-key">
                  <span className="key-label">License Key</span>
                  <span className="key-value">{licenseKey}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      
      {renderSidebar()}
      
      <main className="tutorial-main">
        <div className="tutorial-content">
          <div className={`tutorial-step ${isExiting ? 'exiting' : ''}`}>
            {renderStepContent()}
          </div>
        </div>
      </main>
      
      {renderInfoPanel()}
    </div>
  );
};

export default Tutorial;
